import {
  approvedIslamicComponentDefinitions,
  approvedDailyAyahOptions,
  approvedDailyDuaOptions,
  approvedDailyHadithOptions,
  islamicComponentIds,
  type DailyIslamicContentMode,
  type HijriDateDisplayPreference,
  type IslamicComponentDefinition,
  type IslamicComponentId,
  type OrganizationEntitlementId,
} from "../../../packages/foundation/src/index.js";

export type IslamicComponentEntryRow = {
  enabled: boolean;
  homeEnabled: boolean;
};

export type PlatformIslamicComponentRow = IslamicComponentDefinition;

export type OrganizationIslamicComponentsRow = {
  components: Record<IslamicComponentId, IslamicComponentEntryRow>;
  dailyAyahReference: string | null;
  dailyContentDate: string | null;
  dailyContentMode: DailyIslamicContentMode;
  dailyDuaReference: string | null;
  dailyHadithReference: string | null;
  hijriDateDisplay: HijriDateDisplayPreference;
  updatedAt: Date | undefined;
  updatedBy: string;
};

export type OrganizationIslamicComponentsEditorInput = {
  dailyAyahReference: string;
  dailyContentDate: string;
  enabledComponentIds: IslamicComponentId[];
  homeEnabledComponentIds: IslamicComponentId[];
  dailyContentMode: DailyIslamicContentMode;
  dailyDuaReference: string;
  dailyHadithReference: string;
  hijriDateDisplay: HijriDateDisplayPreference;
};

const p12DefaultEnabledComponentIds: IslamicComponentId[] = [
  "prayer",
  "quran",
  "quranAudio",
  "quranWordByWord",
  "tafsir",
  "dailyAyah",
  "dailyHadith",
  "dailyDua",
  "namesOfAllah",
  "hijriCalendar",
];

const p12DefaultHomeComponentIds: IslamicComponentId[] = [
  "prayer",
  "dailyAyah",
  "dailyHadith",
  "dailyDua",
];

export function organizationIslamicComponentsEditorInputForOrganization(
  _organizationId: string,
): OrganizationIslamicComponentsEditorInput {
  void _organizationId;
  return {
    dailyAyahReference: "",
    dailyContentDate: "",
    dailyContentMode: "automatic",
    dailyDuaReference: "",
    dailyHadithReference: "",
    enabledComponentIds: p12DefaultEnabledComponentIds,
    hijriDateDisplay: "hijriSecondary",
    homeEnabledComponentIds: p12DefaultHomeComponentIds,
  };
}

export function organizationIslamicComponentsEditorInputFromRow(
  row: OrganizationIslamicComponentsRow,
): OrganizationIslamicComponentsEditorInput {
  return {
    dailyAyahReference: row.dailyAyahReference ?? "",
    dailyContentDate: row.dailyContentDate ?? "",
    dailyContentMode: row.dailyContentMode,
    dailyDuaReference: row.dailyDuaReference ?? "",
    dailyHadithReference: row.dailyHadithReference ?? "",
    enabledComponentIds: islamicComponentIds.filter(
      (componentId) => row.components[componentId].enabled,
    ),
    hijriDateDisplay: row.hijriDateDisplay,
    homeEnabledComponentIds: islamicComponentIds.filter(
      (componentId) => row.components[componentId].homeEnabled,
    ),
  };
}

export function organizationIslamicComponentsRowFromSnapshot(snapshot: {
  data(): Record<string, unknown> | undefined;
}): OrganizationIslamicComponentsRow | undefined {
  const data = snapshot.data();
  if (!data) {
    return undefined;
  }

  return organizationIslamicComponentsRowFromData(data);
}

export function organizationIslamicComponentsRowFromData(
  data: Record<string, unknown>,
): OrganizationIslamicComponentsRow {
  const componentsValue = isRecord(data.components) ? data.components : {};
  const defaultEditor = organizationIslamicComponentsEditorInputForOrganization("");
  const defaultEnabled = new Set(defaultEditor.enabledComponentIds);
  const defaultHomeEnabled = new Set(defaultEditor.homeEnabledComponentIds);
  const components = Object.fromEntries(
    islamicComponentIds.map((componentId) => {
      const component = isRecord(componentsValue[componentId]) ? componentsValue[componentId] : {};
      return [
        componentId,
        {
          enabled:
            typeof component.enabled === "boolean"
              ? component.enabled
              : defaultEnabled.has(componentId),
          homeEnabled:
            typeof component.homeEnabled === "boolean"
              ? component.homeEnabled
              : defaultHomeEnabled.has(componentId),
        },
      ];
    }),
  ) as Record<IslamicComponentId, IslamicComponentEntryRow>;

  return {
    components,
    dailyAyahReference: optionalReferenceValue(data.dailyAyahReference),
    dailyContentDate: optionalDateIdValue(data.dailyContentDate),
    dailyContentMode: isDailyIslamicContentMode(data.dailyContentMode)
      ? data.dailyContentMode
      : "automatic",
    dailyDuaReference: optionalReferenceValue(data.dailyDuaReference),
    dailyHadithReference: optionalReferenceValue(data.dailyHadithReference),
    hijriDateDisplay: isHijriDateDisplayPreference(data.hijriDateDisplay)
      ? data.hijriDateDisplay
      : "hijriSecondary",
    updatedAt: optionalDateFromFirestore(data.updatedAt),
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
  };
}

export function platformIslamicComponentRowFromSnapshotData(
  componentId: string,
  data: Record<string, unknown>,
): PlatformIslamicComponentRow | undefined {
  const fallback = approvedIslamicComponentDefinitions.find(
    (component) => component.componentId === componentId,
  );
  if (!fallback) {
    return undefined;
  }

  return {
    ...fallback,
    ...data,
    componentId: fallback.componentId,
    dependencyComponentIds: Array.isArray(data.dependencyComponentIds)
      ? data.dependencyComponentIds.filter((dependencyId): dependencyId is IslamicComponentId =>
          islamicComponentIds.includes(dependencyId as IslamicComponentId),
        )
      : fallback.dependencyComponentIds,
    requiredEntitlementIds: Array.isArray(data.requiredEntitlementIds)
      ? data.requiredEntitlementIds.filter(
          (entitlementId): entitlementId is OrganizationEntitlementId =>
            typeof entitlementId === "string",
        )
      : fallback.requiredEntitlementIds,
    schemaVersion: 1,
    source: "p1_islamic_components",
  };
}

export function organizationIslamicComponentsRequestFromEditor(
  editor: OrganizationIslamicComponentsEditorInput,
  organizationId: string,
): Record<string, unknown> {
  const enabled = new Set(editor.enabledComponentIds);

  return {
    dailyAyahReference: editor.dailyAyahReference || null,
    dailyContentDate: editor.dailyContentDate || null,
    dailyContentMode: editor.dailyContentMode,
    dailyDuaReference: editor.dailyDuaReference || null,
    dailyHadithReference: editor.dailyHadithReference || null,
    enabledComponentIds: editor.enabledComponentIds,
    hijriDateDisplay: editor.hijriDateDisplay,
    homeEnabledComponentIds: editor.homeEnabledComponentIds.filter((componentId) =>
      enabled.has(componentId),
    ),
    organizationId,
  };
}

export function islamicComponentLabel(
  componentId: IslamicComponentId,
  platformComponents: readonly PlatformIslamicComponentRow[],
): string {
  return (
    platformComponents.find((component) => component.componentId === componentId)?.displayName ??
    approvedIslamicComponentDefinitions.find((component) => component.componentId === componentId)
      ?.displayName ??
    componentId
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isDailyIslamicContentMode(value: unknown): value is DailyIslamicContentMode {
  return value === "off" || value === "automatic" || value === "curated";
}

function isHijriDateDisplayPreference(value: unknown): value is HijriDateDisplayPreference {
  return value === "gregorianOnly" || value === "hijriPrimary" || value === "hijriSecondary";
}

function optionalReferenceValue(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function optionalDateIdValue(value: unknown): string | null {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
    ? value.trim()
    : null;
}

export { approvedDailyAyahOptions, approvedDailyDuaOptions, approvedDailyHadithOptions };

function optionalDateFromFirestore(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return value;
  }

  if (isRecord(value) && typeof value.toDate === "function") {
    const date = value.toDate();
    return date instanceof Date ? date : undefined;
  }

  return undefined;
}
